import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLead } from "../../context/LeadContext";
import { scrollToId } from "../../lib/scroll";

const HERO_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_kia-firmenkunden/artifacts/l3sd6jiq_GARAGE%202025klein.webp";

function MaskedLine({ children, delay }) {
  return (
    <span className="mask-line">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { requestOfferte } = useLead();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 200]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.35]);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden bg-neutral-950">
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Künzler & Sauber AG Garage Hinwil"
          className="w-full h-full object-cover"
          data-testid="hero-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32 pt-40 w-full">
        <motion.img
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          src="https://customer-assets-cm19k8pv.emergentagent.net/job_kia-firmenkunden/artifacts/0lvmvxwr_638e307b3eae41f3706b4076.png"
          alt="KIA"
          data-testid="hero-kia-logo"
          className="h-6 sm:h-7 w-auto mb-10"
          style={{ filter: "brightness(0) invert(1)" }}
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-10 h-px bg-white/60" />
          <span className="text-xs uppercase tracking-[0.35em] text-white/80 font-semibold">
            KIA Firmenkunden
          </span>
        </motion.div>

        <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tighter leading-[0.95] font-medium text-white max-w-5xl">
          <MaskedLine delay={0.35}>Attraktive</MaskedLine>
          <MaskedLine delay={0.47}>Flottenkonditionen</MaskedLine>
          <MaskedLine delay={0.59}>für Ihr Unternehmen.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-8 text-base sm:text-lg text-neutral-300 max-w-xl leading-relaxed"
        >
          Künzler &amp; Sauber AG – Ihr persönlicher KIA Partner im Zürcher Oberland.
        </motion.p>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          data-testid="hero-promo-badge"
          onClick={() => scrollToId("konditionen")}
          className="mt-6 inline-flex items-center gap-3 border border-red-500/60 bg-red-600/20 backdrop-blur px-5 py-2.5 text-left transition-colors hover:bg-red-600/30"
        >
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
          <span className="text-sm text-white">
            <strong>Aktuell:</strong> Bis zu 16 % Flottenrabatt auf KIA Neuwagen*
          </span>
          <ArrowRight size={14} className="text-white/70" />
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-cta-offerte"
            onClick={() => requestOfferte()}
            className="group inline-flex items-center justify-center gap-3 bg-white text-neutral-950 font-semibold text-sm px-8 py-4 transition-colors hover:bg-neutral-200"
          >
            Firmenofferte anfordern
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-modelle"
            onClick={() => scrollToId("modelle")}
            className="inline-flex items-center justify-center gap-3 border border-white/40 text-white font-semibold text-sm px-8 py-4 transition-colors hover:bg-white/10"
          >
            KIA Modelle entdecken
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown size={18} />
        </motion.div>
      </motion.div>
    </section>
  );
}
